import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PageHeader } from '../../../shared/components/PageHeader';
import { EmptyState } from '../../../shared/components/EmptyState';
import { ErrorAlert } from '../../../shared/components/ErrorAlert';
import { Spinner } from '../../../shared/components/Spinner';
import { ConfirmModal } from '../../../shared/components/ConfirmModal';
import { useDocumentTitle } from '../../../shared/hooks/useDocumentTitle';
import { useAuth } from '../../../contexts/AuthContext';
import type {
  DiscountCode,
  DiscountCodeCreatePayload,
  DiscountCodeFilters,
  DiscountCodeSortKey,
  DiscountCodeUpdatePayload,
  SortDirection,
} from '../../../models/discountCode';
import {
  createDiscountCode,
  deleteDiscountCode,
  fetchDiscountCodes,
  filterDiscountCodes,
  logDiscountCodeAudit,
  sortDiscountCodes,
  updateDiscountCode,
} from '../../../services/admin/discountCodeService';
import { fetchAdminDashboard } from '../../../services/admin/dashboardService';
import { DiscountCodeTable } from '../components/DiscountCodeTable';
import { DiscountCodeFormModal, type DiscountCodeCompanyOption } from '../components/DiscountCodeFormModal';

const STATUS_OPTIONS = ['all', 'active', 'inactive', 'expired'];

/** Discount codes shown per page. */
const PAGE_SIZE = 10;

export function DiscountCodeListPage(): React.ReactElement {
  useDocumentTitle('Discount codes · Triplicity Backoffice');
  const { getAccessToken, acquireAccessToken, isAuthenticated } = useAuth();

  const [codes, setCodes] = useState<DiscountCode[]>([]);
  const [companies, setCompanies] = useState<DiscountCodeCompanyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [filters, setFilters] = useState<DiscountCodeFilters>({ status: 'all' });
  const [sortKey, setSortKey] = useState<DiscountCodeSortKey>('code');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(1);

  const [formVisible, setFormVisible] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingCode, setEditingCode] = useState<DiscountCode | null>(null);

  const [confirm, setConfirm] = useState<{ kind: 'delete' | 'deactivate'; code: DiscountCode } | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchDiscountCodes(getAccessToken, filters);
      setCodes(rows);
    } catch (err) {
      setCodes([]);
      setError(err instanceof Error ? err.message : 'Failed to load discount codes.');
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, filters]);

  useEffect(() => {
    logDiscountCodeAudit('access', { route: '/admin/discount-codes', authenticated: isAuthenticated });
    void reload();
  }, [reload, isAuthenticated]);

  // Company options for the assignment picker (reuses the dashboard company list).
  useEffect(() => {
    let isMounted = true;
    void (async () => {
      try {
        const rows = await fetchAdminDashboard(acquireAccessToken);
        if (isMounted) {
          setCompanies(rows.map((c) => ({ companyId: c.companyId, companyName: c.companyName })));
        }
      } catch {
        if (isMounted) {
          setCompanies([]);
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [acquireAccessToken]);

  const filteredCodes = useMemo(() => filterDiscountCodes(codes, filters), [codes, filters]);
  const sortedCodes = useMemo(
    () => sortDiscountCodes(filteredCodes, sortKey, sortDirection),
    [filteredCodes, sortKey, sortDirection]
  );

  const totalPages = Math.max(1, Math.ceil(sortedCodes.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pageCodes = useMemo(
    () => sortedCodes.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [sortedCodes, page]
  );

  const rangeStart = sortedCodes.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, sortedCodes.length);

  const handleSort = (key: DiscountCodeSortKey): void => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
    setPage(1);
  };

  const handleFilterChange = (next: DiscountCodeFilters): void => {
    logDiscountCodeAudit('filter', { filters: next });
    setFilters(next);
    setPage(1);
  };

  const openCreate = (): void => {
    setFormMode('create');
    setEditingCode(null);
    setStatusMessage(null);
    setFormVisible(true);
  };

  const openEdit = (code: DiscountCode): void => {
    setFormMode('edit');
    setEditingCode(code);
    setStatusMessage(null);
    setFormVisible(true);
  };

  const handleCreate = async (payload: DiscountCodeCreatePayload): Promise<void> => {
    await createDiscountCode(payload, getAccessToken);
    logDiscountCodeAudit('create', { code: payload.code });
    setStatusMessage(`Discount code “${payload.code}” created.`);
    await reload();
  };

  const handleUpdate = async (id: string, payload: DiscountCodeUpdatePayload): Promise<void> => {
    await updateDiscountCode(id, payload, getAccessToken);
    logDiscountCodeAudit('update', { id });
    setStatusMessage('Discount code updated.');
    await reload();
  };

  const handleToggleStatus = async (code: DiscountCode): Promise<void> => {
    if (code.status === 'active') {
      setConfirm({ kind: 'deactivate', code });
      return;
    }
    await updateDiscountCode(code.id, { status: 'active' }, getAccessToken);
    logDiscountCodeAudit('update', { id: code.id, status: 'active' });
    setStatusMessage(`Discount code “${code.code}” activated.`);
    await reload();
  };

  const handleConfirm = async (): Promise<void> => {
    if (!confirm) {
      return;
    }
    const { kind, code } = confirm;
    if (kind === 'delete') {
      await deleteDiscountCode(code.id, getAccessToken);
      logDiscountCodeAudit('delete', { id: code.id });
      setStatusMessage(`Discount code “${code.code}” deleted.`);
    } else {
      await updateDiscountCode(code.id, { status: 'inactive' }, getAccessToken);
      logDiscountCodeAudit('update', { id: code.id, status: 'inactive' });
      setStatusMessage(`Discount code “${code.code}” deactivated.`);
    }
    setConfirm(null);
    await reload();
  };

  return (
    <div>
      <PageHeader
        title="Discount codes"
        subtitle="Create and manage discount codes assigned to 3PL companies."
        actions={
          <button type="button" className="bo-btn bo-btn-primary" onClick={openCreate}>
            New discount code
          </button>
        }
      />

      <div className="bo-admin-toolbar bo-card">
        <label className="bo-field" htmlFor="discount-status">
          <span className="bo-field-label">Status</span>
          <select
            id="discount-status"
            className="bo-input"
            value={filters.status ?? 'all'}
            onChange={(event) => handleFilterChange({ ...filters, status: event.target.value })}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option === 'all' ? 'All' : option.charAt(0).toUpperCase() + option.slice(1)}
              </option>
            ))}
          </select>
        </label>
        <label className="bo-field" htmlFor="discount-search">
          <span className="bo-field-label">Search</span>
          <input
            id="discount-search"
            className="bo-input"
            placeholder="Code or company"
            value={filters.search ?? ''}
            onChange={(event) => handleFilterChange({ ...filters, search: event.target.value })}
          />
        </label>
      </div>

      {statusMessage ? (
        <p className="bo-form-success" role="status">
          {statusMessage}
        </p>
      ) : null}

      {loading ? (
        <div className="bo-card bo-detail-loading">
          <Spinner label="Loading discount codes…" />
        </div>
      ) : null}

      {!loading && error ? <ErrorAlert message={error} /> : null}

      {!loading && !error && filteredCodes.length === 0 ? (
        <EmptyState
          title="No discount codes"
          description="Create a discount code to assign promotions or billing incentives to 3PL companies."
          action={
            <button type="button" className="bo-btn bo-btn-primary" onClick={openCreate}>
              New discount code
            </button>
          }
        />
      ) : null}

      {!loading && !error && filteredCodes.length > 0 ? (
        <>
          <DiscountCodeTable
            codes={pageCodes}
            sortKey={sortKey}
            sortDirection={sortDirection}
            onSort={handleSort}
            onEdit={openEdit}
            onToggleStatus={(code) => void handleToggleStatus(code)}
            onDelete={(code) => setConfirm({ kind: 'delete', code })}
          />
          <div className="bo-admin-pagination">
            <span className="bo-muted">
              Showing {rangeStart}–{rangeEnd} of {sortedCodes.length}
            </span>
            <div className="bo-admin-pagination-controls">
              <button
                type="button"
                className="bo-btn bo-btn-secondary"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page <= 1}
              >
                Previous
              </button>
              <span className="bo-muted">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className="bo-btn bo-btn-secondary"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </>
      ) : null}

      <DiscountCodeFormModal
        visible={formVisible}
        mode={formMode}
        initial={editingCode}
        companies={companies}
        onClose={() => setFormVisible(false)}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
      />

      <ConfirmModal
        visible={confirm !== null}
        title={confirm?.kind === 'delete' ? 'Delete discount code' : 'Deactivate discount code'}
        message={
          confirm
            ? confirm.kind === 'delete'
              ? `Delete “${confirm.code.code}”? This cannot be undone.`
              : `Deactivate “${confirm.code.code}”? It will no longer be available for use.`
            : ''
        }
        confirmLabel={confirm?.kind === 'delete' ? 'Delete' : 'Deactivate'}
        confirmVariant="danger"
        onClose={() => setConfirm(null)}
        onConfirm={handleConfirm}
      />
    </div>
  );
}